/**
 * Transfer Hub — final handover area.
 *
 * Visible to: the learner AND the selected ADI (+ admin).
 * Shows the Progress Card, the shared chat link, and both confirmation
 * states. When BOTH parties confirm, the handover runs server-side
 * (pupil row repointed, old ADI receives a calm thank-you, progress
 * card snapshotted for audit).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Award, Check, Clock3, Handshake, Info, MessageCircle,
  Sparkles, Target, Trophy, TrendingUp,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ProgressCard({ card }) {
  if (!card) return null;
  return (
    <div
      data-testid="transfer-progress-card"
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
    >
      <div className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-100">
            Progress Card
          </p>
        </div>
        <h2 className="text-base font-bold mt-0.5">
          {card.learner_name || "Learner"}
        </h2>
        <p className="text-[11px] text-indigo-100">
          A snapshot of everything carried forward into this transfer
        </p>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-slate-50 rounded-lg py-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Hours</p>
            <p className="text-xl font-bold text-slate-900">{card.hours_completed || 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg py-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Lessons</p>
            <p className="text-xl font-bold text-slate-900">{card.lesson_count || 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg py-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Gearbox</p>
            <p className="text-sm font-bold text-slate-900 capitalize mt-1">
              {card.transmission || "—"}
            </p>
          </div>
        </div>

        {card.confidence_level != null && (
          <div className="flex items-center gap-2 bg-emerald-50 rounded-lg p-3">
            <TrendingUp className="w-4 h-4 text-emerald-700" />
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                Overall confidence
              </p>
              <p className="text-sm font-bold text-emerald-900">
                {card.confidence_level}/5
              </p>
            </div>
          </div>
        )}

        {card.readiness_level && (
          <div className="flex items-center gap-2 bg-indigo-50 rounded-lg p-3">
            <Target className="w-4 h-4 text-indigo-700" />
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">
                Readiness level
              </p>
              <p className="text-sm font-bold text-indigo-900">{card.readiness_level}</p>
            </div>
          </div>
        )}

        {card.test_date && (
          <div className="flex items-center gap-2 bg-amber-50 rounded-lg p-3">
            <Clock3 className="w-4 h-4 text-amber-700" />
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                Test booked
              </p>
              <p className="text-sm font-bold text-amber-900">{card.test_date}</p>
            </div>
          </div>
        )}

        {card.strengths && card.strengths.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Strong areas
            </p>
            <div className="flex flex-wrap gap-1.5">
              {card.strengths.map((s, i) => (
                <span
                  key={i}
                  data-testid={`progress-strength-${i}`}
                  className="text-[11px] font-semibold bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-1 capitalize"
                >
                  {s.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        )}

        {card.needs_work && card.needs_work.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Focus areas
            </p>
            <div className="flex flex-wrap gap-1.5">
              {card.needs_work.map((s, i) => (
                <span
                  key={i}
                  data-testid={`progress-focus-${i}`}
                  className="text-[11px] font-semibold bg-amber-100 text-amber-700 rounded-full px-2.5 py-1 capitalize"
                >
                  {s.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        )}

        {card.next_lesson_focus && card.next_lesson_focus.length > 0 && (
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Previously planned next lesson
            </p>
            <ul className="text-xs text-slate-700 space-y-0.5 list-disc list-inside">
              {card.next_lesson_focus.slice(0, 4).map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TransferHub() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/transfer-network/requests/${requestId}/hub`,
        { withCredentials: true },
      );
      setHub(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer Hub unavailable");
      goBack();
    } finally {
      setLoading(false);
    }
  }, [requestId, navigate]);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/transfer-network/requests/${requestId}/confirm`,
        {},
        { withCredentials: true },
      );
      if (data?.both_confirmed) {
        toast.success("Transfer complete — everything's carried forward. Enjoy the next chapter!", { duration: 8000 });
      } else {
        toast.success("You've confirmed — waiting for the other side");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't confirm");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-400">Opening Transfer Hub…</p>
      </div>
    );
  }
  if (!hub) return null;
  const { request, progress_card, chat_id, confirmations, viewer_side } = hub;
  const isCompleted = request.status === "completed";
  const mineConfirmed = viewer_side === "learner"
    ? confirmations.learner_confirmed
    : confirmations.adi_confirmed;
  const theirsConfirmed = viewer_side === "learner"
    ? confirmations.adi_confirmed
    : confirmations.learner_confirmed;
  const theirLabel = viewer_side === "learner" ? "your new instructor" : "the learner";

  return (
    <div data-testid="transfer-hub-page" className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="transfer-hub-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900">Transfer Hub</h1>
            <p className="text-[11px] text-slate-500">
              Shared handover area — everything needed to pick up right where you left off
            </p>
          </div>
          {isCompleted && (
            <span
              data-testid="transfer-hub-complete-badge"
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-1"
            >
              <Check className="w-3 h-3" /> Complete
            </span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Completion callout */}
        {isCompleted ? (
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Check className="w-5 h-5" />
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-100">
                Handover complete
              </p>
            </div>
            <h2 className="text-lg font-bold">
              Welcome to your next chapter
            </h2>
            <p className="text-sm text-emerald-100 mt-1 leading-relaxed">
              Every hour, every skill and every ounce of confidence has come with
              {viewer_side === "learner" ? " you" : " your new learner"}. Pick up right
              where you left off.
            </p>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Handshake className="w-5 h-5" />
              <p className="text-xs font-bold uppercase tracking-wider text-indigo-100">
                Final step
              </p>
            </div>
            <h2 className="text-lg font-bold">
              One confirmation from each side and you're all set
            </h2>
            <p className="text-sm text-indigo-100 mt-1 leading-relaxed">
              Review the Progress Card below. When both of you tap "Confirm transfer",
              everything lands in its new home.
            </p>
          </div>
        )}

        {/* Progress Card */}
        <ProgressCard card={progress_card} />

        {/* Both-side confirmation state */}
        {!isCompleted && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  confirmations.learner_confirmed
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {confirmations.learner_confirmed ? <Check className="w-4 h-4" /> : <Clock3 className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Learner confirmation</p>
                <p className="text-[11px] text-slate-500">
                  {confirmations.learner_confirmed ? "Confirmed" : "Waiting"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  confirmations.adi_confirmed
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {confirmations.adi_confirmed ? <Check className="w-4 h-4" /> : <Clock3 className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Instructor confirmation</p>
                <p className="text-[11px] text-slate-500">
                  {confirmations.adi_confirmed ? "Confirmed" : "Waiting"}
                </p>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
              {chat_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/learn/transfer-chat/${encodeURIComponent(chat_id)}`)}
                  data-testid="transfer-hub-open-chat"
                  className="inline-flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-sm rounded-xl px-4 py-2.5"
                >
                  <MessageCircle className="w-4 h-4" />
                  Open Match Chat
                </button>
              )}
              <button
                type="button"
                onClick={confirm}
                disabled={confirming || mineConfirmed}
                data-testid="transfer-hub-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl px-4 py-2.5 disabled:bg-emerald-300"
              >
                <Check className="w-4 h-4" />
                {mineConfirmed
                  ? (theirsConfirmed ? "Completing…" : `Waiting for ${theirLabel}`)
                  : "Confirm transfer"}
              </button>
            </div>
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-2 text-[11px] text-slate-600 leading-relaxed">
          <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <p>
            Anything prepaid with your previous instructor is resolved directly between
            you both — we don't take a cut and we don't get in the way.
          </p>
        </div>
      </div>
    </div>
  );
}
